// src/pages/DeliverPage.js
// Superintendent/foreman screen for delivering tools from a truck to a jobsite.
// Step 1: Pick truck → Step 2: See manifest → Step 3: Assign each item to a person → Confirm
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { getLocations, getItems, getWorkers, createTransaction } from '../services/api';
import JobPicker from '../components/JobPicker';

export default function DeliverPage() {
  const navigate = useNavigate();
  const { worker } = useContext(AuthContext);

  const [step, setStep] = useState('truck');       // 'truck' | 'manifest' | 'confirm'
  const [trucks, setTrucks] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [manifest, setManifest] = useState([]);    // items on the truck
  const [workers, setWorkers] = useState([]);
  // assignments: { [item_id]: worker_object }
  const [assignments, setAssignments] = useState({});
  // Where each project's tools are being delivered (10/29). Matched to the job
  // from the project they were loaded for; Change picks another, with "You're
  // here" suggestions. Before this, delivered tools kept the truck as their
  // location. { [project]: { loc, source } }
  const [jobsites, setJobsites] = useState([]);
  const [groupJobs, setGroupJobs] = useState({});
  const [pickingFor, setPickingFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingManifest, setLoadingManifest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);    // { delivered: [], failed: [] }
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      getLocations({ type: 'truck' }),
      getWorkers(),
      getLocations({ type: 'jobsite' }),
    ]).then(([trucksRes, workersRes, jobsRes]) => {
      setTrucks(trucksRes.data.locations || []);
      setWorkers(workersRes.data.workers || []);
      setJobsites(jobsRes.data.locations || []);
    }).catch(() => setError('Failed to load data')).finally(() => setLoading(false));
  }, []);

  const selectTruck = async (truck) => {
    setSelectedTruck(truck);
    setLoadingManifest(true);
    setError('');
    try {
      const res = await getItems({ status: 'in_transit', location: truck.location_id, limit: 100 });
      const items = res.data.items || [];
      setManifest(items);
      setAssignments({});
      // Start each project group at the job it was loaded for, when it can be found.
      const start = {};
      for (const { project, items: list } of groupByProject(items)) {
        const it = list[0];
        const loc = jobsites.find(l =>
          (it.current_project_id && String(l.procore_project_id) === String(it.current_project_id)) ||
          l.name === project || l.procore_project_name === project);
        if (loc) start[project] = { loc, source: null };
      }
      setGroupJobs(start);
      setStep('manifest');
    } catch {
      setError('Failed to load truck manifest.');
    } finally {
      setLoadingManifest(false);
    }
  };

  const assignWorker = (itemId, w) => {
    setAssignments(prev => ({ ...prev, [itemId]: w }));
  };

  const allAssigned = manifest.length > 0 && manifest.every(i => assignments[i.item_id]);
  const allPlaced = groupByProject(manifest).every(g => groupJobs[g.project]);

  const handleDeliver = async () => {
    setSubmitting(true);
    setError('');
    const delivered = [];
    const failed = [];

    for (const item of manifest) {
      const assignedTo = assignments[item.item_id];
      if (!assignedTo) { failed.push({ item, reason: 'No person assigned' }); continue; }
      const job = groupJobs[item.current_project_name || 'No Project Assigned'];
      try {
        await createTransaction({
          item_id:              item.item_id,
          transaction_type:     'deliver_site',
          performed_by:         worker.worker_id,
          assigned_to:          assignedTo.worker_id,
          to_location_id:       job?.loc.location_id || item.current_location_id || selectedTruck.location_id,
          quantity:             1,
          procore_project_id:   item.current_project_id   || job?.loc.procore_project_id || null,
          procore_project_name: item.current_project_name || job?.loc.procore_project_name || job?.loc.name || null,
          job_pick_source:      job?.source || null,
          notes: `Delivered from ${selectedTruck.name} to ${assignedTo.display_name}${item.current_project_name ? ` · ${item.current_project_name}` : ''}`,
        });
        delivered.push({ item, assignedTo });
      } catch (err) {
        failed.push({ item, reason: err.response?.data?.error || 'Unknown error' });
      }
    }

    setResults({ delivered, failed });
    setSubmitting(false);
  };

  // ── Loading ──────────────────────────────────────────────
  if (loading) return (
    <div className="screen">
      <div className="loading"><div className="spinner" /><span>Loading...</span></div>
    </div>
  );

  // ── Results ──────────────────────────────────────────────
  if (results) {
    return (
      <div className="screen">
        <div className="screen-header">
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2>Delivery Complete</h2>
        </div>
        <div className="screen-content">
          {results.delivered.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 12, color: '#22c55e' }}>✅ Delivered ({results.delivered.length})</h3>
              {results.delivered.map(({ item, assignedTo }) => (
                <div key={item.item_id} className="item-row" style={{ cursor: 'default' }}>
                  <div className="item-row-icon">🔧</div>
                  <div className="item-row-info">
                    <h4>{item.name}</h4>
                    <p>→ {assignedTo.display_name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {results.failed.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 12, color: 'var(--status-lost)' }}>⚠️ Failed ({results.failed.length})</h3>
              {results.failed.map(({ item, reason }) => (
                <div key={item.item_id} className="item-row" style={{ cursor: 'default' }}>
                  <div className="item-row-icon">⚠️</div>
                  <div className="item-row-info">
                    <h4>{item.name}</h4>
                    <p style={{ color: 'var(--status-lost)' }}>{reason}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button className="btn btn-primary" onClick={() => navigate('/')}>Back to Home</button>
          <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => {
            setStep('truck'); setSelectedTruck(null); setManifest([]);
            setAssignments({}); setGroupJobs({}); setResults(null);
          }}>Deliver Another Truck</button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <button onClick={() => {
          if (step === 'manifest') { setStep('truck'); setSelectedTruck(null); setManifest([]); }
          else navigate(-1);
        }} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h2>{step === 'truck' ? 'Select Truck' : `Deliver from ${selectedTruck?.name}`}</h2>
          {step === 'manifest' && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{manifest.length} item{manifest.length !== 1 ? 's' : ''} on board</p>
          )}
        </div>
      </div>

      <div className="screen-content">
        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

        {/* ── Step 1: Truck picker ── */}
        {step === 'truck' && (
          trucks.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '2rem' }}>🚛</div>
              <p>No trucks found. Add truck locations in Admin first.</p>
            </div>
          ) : (
            <div className="card">
              {trucks.map(t => (
                <div key={t.location_id} className="item-row" onClick={() => !loadingManifest && selectTruck(t)} style={{ cursor: 'pointer' }}>
                  <div className="item-row-icon">🚛</div>
                  <div className="item-row-info">
                    <h4>{t.name}</h4>
                    <p>{t.item_count || 0} items currently loaded</p>
                  </div>
                  {loadingManifest ? (
                    <div className="spinner" style={{ width: 20, height: 20 }} />
                  ) : (
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {/* ── Step 2: Manifest with person pickers ── */}
        {step === 'manifest' && (
          <>
            {manifest.length === 0 ? (
              <div className="empty-state">
                <div style={{ fontSize: '2rem' }}>📦</div>
                <p>No in-transit items on {selectedTruck?.name}.</p>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Items must be loaded onto this truck first.</p>
              </div>
            ) : (
              <>
                {/* Group by project */}
                {groupByProject(manifest).map(({ project, items }) => (
                  <div key={project} style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                      📍 {project}
                    </p>
                    <div className="card" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Deliver to</p>
                        <p style={{ fontWeight: 700, color: groupJobs[project] ? 'var(--text-primary)' : 'var(--status-in-repair)' }}>
                          {groupJobs[project] ? groupJobs[project].loc.name : 'Choose the job'}
                        </p>
                      </div>
                      <button className="btn btn-ghost" style={{ width: 'auto', minHeight: 34, padding: '0 12px', fontSize: '0.85rem' }}
                        onClick={() => setPickingFor(project)}>{groupJobs[project] ? 'Change' : 'Choose'}</button>
                    </div>
                    <div className="card">
                      {items.map(item => (
                        <ManifestItem
                          key={item.item_id}
                          item={item}
                          workers={workers}
                          assigned={assignments[item.item_id]}
                          onAssign={(w) => assignWorker(item.item_id, w)}
                        />
                      ))}
                    </div>
                  </div>
                ))}

                <button
                  className="btn btn-primary"
                  onClick={handleDeliver}
                  disabled={!allAssigned || !allPlaced || submitting}
                  style={{ marginTop: 8 }}
                >
                  {submitting ? 'Delivering...'
                    : !allPlaced ? 'Choose the job for each group first'
                    : allAssigned ? `✅ Deliver All ${manifest.length} Items` : `Assign all items first (${Object.keys(assignments).length}/${manifest.length} done)`}
                </button>

                {pickingFor && (
                  <div className="modal-overlay" onClick={() => setPickingFor(null)}>
                    <div className="modal-sheet" onClick={e => e.stopPropagation()}>
                      <div className="modal-handle" />
                      <h3 style={{ marginBottom: 4 }}>Deliver To</h3>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>Tools loaded for {pickingFor}</p>
                      <JobPicker locations={jobsites} forWorker={worker} selectedId={groupJobs[pickingFor]?.loc.location_id}
                        onPick={(l, source) => { setGroupJobs(prev => ({ ...prev, [pickingFor]: { loc: l, source } })); setPickingFor(null); }} />
                      <button className="btn btn-ghost" style={{ marginTop: 10 }} onClick={() => setPickingFor(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── ManifestItem — one item row with inline person picker ──────
function ManifestItem({ item, workers, assigned, onAssign }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = workers.filter(w =>
    search.trim().length < 1 ||
    w.display_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ borderBottom: '1px solid var(--border)', padding: '12px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.name}</p>
          {item.asset_tag && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.asset_tag}</span>}
        </div>
        <button
          onClick={() => setOpen(!open)}
          style={{
            background: assigned ? '#22c55e22' : 'var(--bg-secondary)',
            border: `1px solid ${assigned ? '#22c55e' : 'var(--border)'}`,
            borderRadius: 8, padding: '6px 12px',
            color: assigned ? '#22c55e' : 'var(--text-secondary)',
            fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {assigned ? `✓ ${assigned.display_name}` : '+ Assign'}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 10 }}>
          <input
            className="input"
            placeholder="Search workers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ marginBottom: 8, fontSize: '0.85rem' }}
            autoFocus
          />
          <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
            {filtered.slice(0, 20).map(w => (
              <div
                key={w.worker_id}
                onClick={() => { onAssign(w); setOpen(false); setSearch(''); }}
                style={{
                  padding: '10px 14px', cursor: 'pointer',
                  borderBottom: '1px solid var(--border)',
                  background: assigned?.worker_id === w.worker_id ? 'var(--bg-secondary)' : 'transparent',
                  fontSize: '0.875rem',
                }}
              >
                <span style={{ fontWeight: 600 }}>{w.display_name}</span>
                <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{w.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function groupByProject(items) {
  const map = {};
  for (const item of items) {
    const key = item.current_project_name || 'No Project Assigned';
    if (!map[key]) map[key] = [];
    map[key].push(item);
  }
  return Object.entries(map).map(([project, items]) => ({ project, items }));
}
